/**
 * Shared notification display logic for the hospital shift/applicant
 * notification surfaces (the bell popover `NotificationPanel` and the full
 * `/notifications` page). Previously each surface re-implemented classification,
 * canceled-status detection, time formatting, the read flag, and click routing,
 * which drifted apart over time. This module is the single source of truth for
 * that logic so both surfaces behave identically.
 *
 * Note: social engagement notifications use a separate system
 * (`socialNotificationUtils.ts`); this module is for shift/applicant inbox items.
 */

/** Minimal shape this module relies on (a subset of RealtimeNotification). */
export interface NotificationLike {
  messageEventType?: string;
  status?: string | number;
  isRead?: boolean;
  read?: boolean;
  appliedShiftDays?: Array<{
    status?: string | number;
    shiftsDaysId?: string;
    shiftDate?: string;
    startTime?: number;
    endTime?: number;
  }>;
  // Click-routing fields — must be real shift UUIDs, never notification activity ids
  shiftId?: string;
  shiftsId?: string;
  relatedId?: string;
  activityId?: string;
  shiftsDaysId?: string;
  shiftDate?: string;
  isShiftOpen?: boolean;
  message?: string;
  data?: Record<string, any>;
  doctorUserId?: string;
  ordersId?: string;
}

export const CANCELED_STATUSES = [
  "Cancelled",
  "User Cancelled",
  "Account Cancelled",
];

/** Unread state — backend/hook populate both `isRead` and `read`; prefer `isRead`. */
export function isNotificationRead(n: NotificationLike): boolean {
  return Boolean(n.isRead ?? n.read ?? false);
}

export function timeAgo(timestamp: number): string {
  const now = Date.now();
  const diff = Math.max(0, now - timestamp);
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
}

export function formatTimestamp(timestamp: number): string {
  if (!timestamp) return "";
  return new Intl.DateTimeFormat("en-AU", {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(timestamp));
}

/**
 * Split a concatenated notification shiftDate string into day entries.
 * Do NOT split on bare commas — dates look like "Mon, 02 Sep 2025 ...".
 */
export function splitNotificationShiftDates(shiftDate?: string): string[] {
  if (!shiftDate) return [];
  const matches = shiftDate.match(
    /[A-Za-z]{3},\s*\d{1,2}\s+[A-Za-z]{3}\s+\d{4}[^,]*(?:,\s*(?=[A-Za-z]{3},\s*\d)|\s*$)/g,
  );
  if (matches && matches.length > 0) {
    return matches.map((m) => m.replace(/,\s*$/, "").trim()).filter(Boolean);
  }
  // ISO-ish fallback: "2026-09-02, 2026-09-03"
  if (/^\d{4}-\d{2}-\d{2}/.test(shiftDate)) {
    return shiftDate
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return [shiftDate.trim()].filter(Boolean);
}

export function formatNotificationDayLabel(dStr: string): string {
  if (!dStr) return "";
  const multiPatternMatch = dStr.match(
    /^([a-zA-Z]{3},\s\d+\s[a-zA-Z]{3}\s\d{4})/,
  );
  if (multiPatternMatch?.[1]) return multiPatternMatch[1];

  const p = dStr.split("-");
  let d = new Date(dStr);
  if (p.length === 3) {
    d = new Date(parseInt(p[0], 10), parseInt(p[1], 10) - 1, parseInt(p[2], 10));
  }
  if (!isNaN(d.getTime())) {
    return new Intl.DateTimeFormat("en-GB", {
      day: "numeric",
      month: "short",
    }).format(d);
  }
  return dStr;
}

export function isPendingAppliedShiftDay(d?: {
  status?: string | number;
  startTime?: number;
}): boolean {
  if (!d) return false;
  const st = String(d.status || "");
  const stLower = st.toLowerCase();
  if (CANCELED_STATUSES.includes(st) || stLower.includes("cancel")) return false;
  if (stLower.includes("confirm")) return false;
  return true;
}

export function summarizeNotificationDates(
  n: NotificationLike,
): { label: string; count: number } {
  const tMillis = new Date().setHours(0, 0, 0, 0);
  const fromApplied = Array.isArray(n.appliedShiftDays)
    ? n.appliedShiftDays
        .filter((d) => {
          if (!isPendingAppliedShiftDay(d)) return false;
          const ts =
            d.startTime && d.startTime > 10000000000
              ? d.startTime
              : d.startTime
                ? d.startTime * 1000
                : 0;
          return !ts || ts >= tMillis;
        })
        .map((d) => d.shiftDate || "")
        .filter(Boolean)
    : [];

  const dates =
    fromApplied.length > 0
      ? fromApplied
      : splitNotificationShiftDates(n.shiftDate);

  if (dates.length === 0) return { label: "", count: 0 };
  if (dates.length === 1) {
    return { label: formatNotificationDayLabel(dates[0]), count: 1 };
  }
  return {
    label: `${formatNotificationDayLabel(dates[0])} +${dates.length - 1} more`,
    count: dates.length,
  };
}

export interface NotificationFlags {
  isActuallyCanceled: boolean;
  isAlert: boolean;
  isPreCredential: boolean;
  isNewApplication: boolean;
  isEmploymentClaim: boolean;
  isShiftApproaching: boolean;
}

/**
 * Classifies a notification into mutually-prioritized buckets used for icon,
 * color, and badge styling. Priority order: canceled/alert > pre-credential >
 * new application > shift approaching > default.
 */
export function classifyNotification(n: NotificationLike): NotificationFlags {
  const eventType = n.messageEventType?.toUpperCase() || "";

  const isStatusCanceled = Boolean(
    n.status && CANCELED_STATUSES.includes(String(n.status)),
  );
  const areAllDaysCanceled = n.appliedShiftDays?.length
    ? n.appliedShiftDays.every((d) =>
        CANCELED_STATUSES.includes(String(d.status || "")),
      )
    : false;
  const isActuallyCanceled =
    isStatusCanceled ||
    areAllDaysCanceled ||
    eventType.includes("CANCELLED") ||
    eventType.includes("WITHDRAWN");

  const isAlert =
    eventType.includes("ALERT") ||
    eventType.includes("REJECTED") ||
    isActuallyCanceled;

  // Pre-credential application types also contain "APPLICATION"; classify them
  // first so they don't fall into the shift "New Application" bucket.
  const isPreCredential = eventType.includes("PRE_CREDENTIAL");
  const isEmploymentClaim = eventType.includes("EMPLOYMENT_CLAIMED");

  const isNewApplication =
    !isAlert &&
    !isPreCredential &&
    !isEmploymentClaim &&
    (eventType.includes("APPLICATION") ||
      eventType.includes("NEW_APPLICATION"));

  const isShiftApproaching =
    eventType.includes("SHIFT_APPROACHING") || eventType.includes("URGENT");

  return {
    isActuallyCanceled,
    isAlert,
    isPreCredential,
    isNewApplication,
    isEmploymentClaim,
    isShiftApproaching,
  };
}

export type NotificationTarget =
  | { kind: "route"; to: string }
  | {
      kind: "shift-modal";
      shiftsId: string;
      /** Optional — ShiftDetailsPopup resolves a day from shiftsId when missing */
      shiftsDaysId?: string;
      /** Auto-open this doctor application after the shift loads */
      openApplicantUserId?: string;
      ordersId?: string;
      openApplicantsTab?: boolean;
    }
  | { kind: "calendar"; openShiftId?: string; shiftDate?: string }
  | { kind: "none" };

function looksLikeShiftUuid(value: unknown): boolean {
  const id = String(value || "").trim();
  if (!id) return false;
  // Never treat bare shift numbers ("42") or numeric notification ids as shift UUIDs.
  if (/^\d+$/.test(id)) return false;
  // Common StatDoctor shift ids: sh-..., shifts-..., or long opaque tokens.
  return id.length >= 8;
}

/**
 * Resolve the real shiftsId from the notification payload.
 * Do NOT fall back to activityId or "shift #N" from the message text.
 */
export function resolveNotificationShiftsId(n: NotificationLike): string {
  const data = n.data || {};
  // Only real shift id fields — never activityId / message "shift #N".
  const candidates = [
    n.shiftsId,
    data.shiftsId,
    n.shiftId,
    data.shiftId,
    // relatedId is only trusted when it looks like a shift id (backend mirrors shiftsId here)
    n.relatedId,
    data.relatedId,
  ];
  for (const candidate of candidates) {
    if (looksLikeShiftUuid(candidate)) {
      return String(candidate).trim();
    }
  }
  return "";
}

export function resolveNotificationShiftsDaysId(
  n: NotificationLike,
  opts: { targetShiftDayId?: string } = {},
): string {
  const data = n.data || {};
  const canceled = new Set(
    CANCELED_STATUSES.map((s) => s.toLowerCase()).concat(["canceled", "cancelled"]),
  );
  const isPendingDay = (d?: {
    status?: string | number;
    shiftsDaysId?: string;
  }) => {
    if (!d?.shiftsDaysId) return false;
    const st = String(d.status || "").toLowerCase();
    if (!st) return true;
    if (canceled.has(st) || st.includes("cancel")) return false;
    if (st.includes("confirm")) return false;
    return true;
  };

  if (opts.targetShiftDayId) {
    return String(opts.targetShiftDayId).trim();
  }

  // Prefer a still-pending day from this notification's applied days
  // (skip already-accepted days from earlier applications on the same group).
  if (Array.isArray(n.appliedShiftDays) && n.appliedShiftDays.length > 0) {
    const pending = n.appliedShiftDays.find(isPendingDay);
    if (pending?.shiftsDaysId) {
      return String(pending.shiftsDaysId).trim();
    }
    const any = n.appliedShiftDays
      .map((d) => String(d?.shiftsDaysId || "").trim())
      .find(Boolean);
    if (any) return any;
  }

  const fromCsv =
    typeof data.shiftsDaysIds === "string"
      ? String(data.shiftsDaysIds)
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
      : [];

  const candidates = [
    n.shiftsDaysId,
    data.shiftsDaysId,
    data.shiftDayId,
    data.shiftDaysId,
    ...fromCsv,
  ];
  for (const candidate of candidates) {
    const id = String(candidate || "").trim();
    if (id) return id;
  }
  return "";
}

/**
 * Resolves where a notification click should navigate.
 * Application notifications open ShiftDetailsPopup using the notification's
 * shiftsId (+ day id when present). No guessing with activity ids / shift numbers.
 */
export function resolveNotificationTarget(
  n: NotificationLike,
  opts: { targetDate?: string; targetShiftDayId?: string } = {},
): NotificationTarget {
  const eventType = n.messageEventType?.toUpperCase() || "";
  const data = n.data || {};

  const shiftsId = resolveNotificationShiftsId(n);
  const shiftsDaysId = resolveNotificationShiftsDaysId(n, opts);

  let firstDateStr = opts.targetDate;
  if (!firstDateStr && n.shiftDate) {
    firstDateStr = n.shiftDate.split(",")[0]?.trim();
    if (firstDateStr && firstDateStr.length < 5) firstDateStr = n.shiftDate;
  }

  if (eventType.includes("PRE_CREDENTIAL")) {
    return { kind: "route", to: "/hire?panel=pre-credential" };
  }

  if (eventType.includes("EMPLOYMENT_CLAIMED")) {
    return { kind: "route", to: "/bank?requests=1" };
  }

  const applicantUserId = String(
    n.doctorUserId || data.doctorUserId || data.userId || data.doctorId || "",
  ).trim();
  const ordersId = String(n.ordersId || data.ordersId || data.orderId || "").trim();

  const isApplicationLike =
    eventType.includes("APPLICATION") ||
    eventType.includes("NEW_APPLICATION") ||
    eventType.includes("WITHDRAWN") ||
    eventType.includes("INVITATION_RESPONSE") ||
    eventType.includes("SHIFT_ACCEPTED") ||
    eventType === "ACCEPTED";

  // One rule: if the notification has a real shiftsId, open Shift Details from it.
  // Do not invent IDs or bounce through calendar/hire for the same payload.
  if (shiftsId) {
    return {
      kind: "shift-modal",
      shiftsId,
      shiftsDaysId: shiftsDaysId || undefined,
      openApplicantUserId: isApplicationLike
        ? applicantUserId || undefined
        : undefined,
      ordersId: isApplicationLike ? ordersId || undefined : undefined,
      openApplicantsTab: isApplicationLike,
    };
  }

  if (eventType.includes("CANCELLED") || eventType.includes("SHIFT_APPROACHING")) {
    return { kind: "calendar", shiftDate: firstDateStr };
  }

  if (isApplicationLike) {
    return { kind: "route", to: "/hire?panel=shifts" };
  }

  if (
    eventType.includes("LEAVE_REQUESTED") ||
    eventType.includes("LEAVE_APPROVED") ||
    eventType.includes("LEAVE_DECLINED")
  ) {
    return { kind: "route", to: "/leave" };
  }

  if (eventType.includes("STAFF_LICENCE") || eventType.includes("LICENCE_CHANGED")) {
    return { kind: "route", to: "/bank" };
  }

  if (
    eventType.includes("SWAP_REQUESTED") ||
    eventType.includes("ROSTER") ||
    eventType.includes("INTERNAL_SHIFT")
  ) {
    return { kind: "route", to: "/roster" };
  }

  return { kind: "route", to: "/notifications" };
}
